import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { reportsApi } from '../../api';
import { ReportExportMenu } from './SalesInventoryReportsPage';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

type Tab = 'pnl' | 'balance-sheet' | 'cash-flow' | 'ap-aging' | 'ar-aging'
  | 'headcount' | 'turnover' | 'attendance' | 'payroll';

const TABS: { key: Tab; label: string; group: 'Finance' | 'HR' }[] = [
  { key: 'pnl',           label: 'P&L',              group: 'Finance' },
  { key: 'balance-sheet', label: 'Balance sheet',    group: 'Finance' },
  { key: 'cash-flow',     label: 'Cash flow',        group: 'Finance' },
  { key: 'ap-aging',      label: 'AP aging',         group: 'Finance' },
  { key: 'ar-aging',      label: 'AR aging',         group: 'Finance' },
  { key: 'headcount',     label: 'Headcount',        group: 'HR' },
  { key: 'turnover',      label: 'Turnover',         group: 'HR' },
  { key: 'attendance',    label: 'Attendance',       group: 'HR' },
  { key: 'payroll',       label: 'Payroll register', group: 'HR' },
];

const FINHR_TYPE_MAP: Record<string, string> = {
  'pnl': 'finance.pnl',
  'balance-sheet': 'finance.balance-sheet',
  'cash-flow': 'finance.cash-flow',
  'ap-aging': 'finance.ap-aging',
  'ar-aging': 'finance.ar-aging',
  'headcount': 'hr.headcount',
  'turnover': 'hr.turnover',
  'attendance': 'hr.attendance',
  'payroll': 'hr.payroll',
};

// Build a superset of params so each report's fetch finds the keys it reads
// (financial statements use fromDate/toDate/asOfDate; operational use startDate/endDate/asOfDate).
function finhrExportParams(tab: string, d: { startDate: string; endDate: string; asOfDate: string }) {
  return {
    startDate: d.startDate, endDate: d.endDate,
    fromDate: d.startDate, toDate: d.endDate,
    asOfDate: d.asOfDate,
  };
}

function defaultRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}
function fmtInt(n: number) { return n.toLocaleString(); }

function formatPeriod(startDate: string, endDate: string) {
  const s = new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const e = new Date(endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} → ${e}`;
}
function formatAsOf(d: string) {
  return `As of ${new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

/** FR-REP-03 / 04 — Finance + HR reports */
export function FinanceHrReportsPage() {
  const initialTab = (new URLSearchParams(window.location.search).get('tab') ?? '') as Tab;
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.key === initialTab) ? initialTab : 'pnl'
  );
  const def = defaultRange();
  const [startDate, setStartDate] = useState(def.start);
  const [endDate, setEndDate] = useState(def.end);
  const [asOfDate, setAsOfDate] = useState(def.end);

  const asOfTabs = ['balance-sheet', 'ap-aging', 'ar-aging', 'headcount'];
  const isAsOfTab = asOfTabs.includes(tab);

  const grouped: Record<'Finance' | 'HR', typeof TABS> = { Finance: [], HR: [] };
  TABS.forEach((t) => grouped[t.group].push(t));
  const activeGroup = TABS.find((t) => t.key === tab)?.group ?? 'Finance';

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow={`Reports · ${activeGroup}`}
        title="Finance & HR reports"
        subtitle="Financial statements, AR/AP aging, and HR analytics across a configurable period."
      />

      {/* Date controls */}
      <div className="card p-4 mb-6 flex gap-4 items-end flex-wrap">
        {isAsOfTab ? (
          <div>
            <label className="label">As of date</label>
            <input type="date" className="input w-44" value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)} />
          </div>
        ) : (
          <>
            <div>
              <label className="label">Start date</label>
              <input type="date" className="input w-44" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="label">End date</label>
              <input type="date" className="input w-44" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </>
        )}
        <div className="flex-1" />
        <ReportExportMenu
          reportType={FINHR_TYPE_MAP[tab]}
          params={finhrExportParams(tab, { startDate, endDate, asOfDate })}
        />
      </div>

      {/* Grouped pill tabs */}
      <div className="mb-6 flex gap-2 flex-wrap items-center">
        {(['Finance', 'HR'] as const).map((group) => (
          <div key={group} className="flex items-center gap-1.5">
            <div className="eyebrow text-ink-400">{group}</div>
            <div className="card p-1 inline-flex gap-1 flex-wrap">
              {grouped[group].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={clsx(
                    'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                    tab === t.key
                      ? 'bg-canvas-subtle text-ink-900'
                      : 'text-ink-600 hover:bg-canvas-subtle'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {tab === 'pnl' && <PnlReport startDate={startDate} endDate={endDate} />}
      {tab === 'balance-sheet' && <BalanceSheetReport asOfDate={asOfDate} />}
      {tab === 'cash-flow' && <CashFlowReport startDate={startDate} endDate={endDate} />}
      {tab === 'ap-aging' && <AgingReport side="ap" asOfDate={asOfDate} />}
      {tab === 'ar-aging' && <AgingReport side="ar" asOfDate={asOfDate} />}
      {tab === 'headcount' && <HeadcountReport />}
      {tab === 'turnover' && <TurnoverHrReport startDate={startDate} endDate={endDate} />}
      {tab === 'attendance' && <AttendanceReport startDate={startDate} endDate={endDate} />}
      {tab === 'payroll' && <PayrollRegisterReport />}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function ReportHeader({ title, period, balanced }: { title: string; period?: string; balanced?: boolean }) {
  return (
    <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
      <div>
        <div className="eyebrow text-ink-500 mb-0.5">Financial statement</div>
        <h2 className="font-display font-bold text-2xl text-ink-900 tracking-tight">{title}</h2>
        {period && (
          <div className="font-mono text-sm text-ink-500 mt-1 tabular-nums">{period}</div>
        )}
      </div>
      {balanced !== undefined && (
        <div className={clsx(
          'inline-flex items-center font-semibold text-xs uppercase tracking-wider px-3 py-1 rounded-pill ring-1 ring-inset',
          balanced
            ? 'bg-sage-50 text-sage-700 ring-sage-200'
            : 'bg-red-50 text-red-700 ring-red-200'
        )}>
          {balanced ? '✓ Balanced' : '✗ Out of balance'}
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: string | number;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-ink-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

// ============================================================
// P&L
// ============================================================
function PnlReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-pnl', startDate, endDate],
    queryFn: () => reportsApi.financePnl({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8 max-w-3xl">
      <ReportHeader title="Income Statement" period={formatPeriod(startDate, endDate)} />

      <FinanceSection title="Revenue" accents="sage" accounts={d.revenue.accounts} total={d.revenue.total} />

      <div className="mt-6">
        <FinanceSection title="Cost of goods sold" accents="orange" accounts={d.cogs.accounts} total={d.cogs.total} />
      </div>

      {/* Gross profit */}
      <div className="mt-6 pt-3 border-t-2 border-ink-300 flex justify-between items-baseline">
        <span className="font-semibold text-ink-900">Gross profit</span>
        <span className="font-mono font-semibold text-lg text-ink-900 tabular-nums">
          {fmtMoney(d.grossProfit)}
        </span>
      </div>

      <div className="mt-6">
        <FinanceSection title="Operating expenses" accents="danger" accounts={d.expenses.accounts} total={d.expenses.total} />
      </div>

      {/* Net income */}
      <div className="mt-6 pt-4 border-t-4 border-ink-900 flex justify-between items-baseline">
        <span className="font-display font-bold text-ink-900">Net income</span>
        <span className={clsx(
          'font-mono font-display font-bold text-2xl tabular-nums tracking-tight',
          d.netIncome >= 0 ? 'text-sage-700' : 'text-danger'
        )}>
          {fmtMoney(d.netIncome)}
        </span>
      </div>
    </div>
  );
}

function FinanceSection({
  title, accents = 'default', accounts, total,
}: {
  title: string;
  accents?: 'default' | 'sage' | 'orange' | 'amber' | 'danger' | 'purple';
  accounts: any[];
  total: number;
}) {
  if (accounts.length === 0) return null;
  const headingTone = {
    default: 'text-ink-700',
    sage:    'text-sage-700',
    orange:  'text-orange-700',
    amber:   'text-amber-700',
    danger:  'text-danger',
    purple:  'text-purple-700',
  }[accents];
  return (
    <div>
      <h3 className={clsx('eyebrow border-b border-ink-300 pb-1 mb-2', headingTone)}>{title}</h3>
      <div className="divide-y divide-ink-100">
        {accounts.map((a) => (
          <div key={a.code} className="flex justify-between py-1.5 pl-2 hover:bg-canvas-subtle/60 rounded">
            <div className="flex gap-3 min-w-0">
              <span className="font-mono text-xs text-ink-500 tabular-nums flex-shrink-0 w-20">{a.code}</span>
              <span className="text-sm text-ink-800 truncate">{a.name}</span>
            </div>
            <span className="font-mono text-sm tabular-nums text-ink-900 flex-shrink-0">
              {fmtMoney(a.amount)}
            </span>
          </div>
        ))}
      </div>
      <div className="flex justify-between font-semibold text-sm border-t border-ink-300 pt-2 mt-1">
        <span className="text-ink-900">Total {title.toLowerCase()}</span>
        <span className="font-mono tabular-nums text-ink-900">{fmtMoney(total)}</span>
      </div>
    </div>
  );
}

// ============================================================
// Balance sheet
// ============================================================
function BalanceSheetReport({ asOfDate }: { asOfDate: string }) {
  const q = useQuery({
    queryKey: ['rep-bs', asOfDate],
    queryFn: () => reportsApi.financeBalanceSheet({ asOfDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8">
      <ReportHeader title="Balance Sheet" period={formatAsOf(asOfDate)} balanced={d.balanced} />

      {!d.balanced && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-800">
          <strong>Imbalance detected:</strong> {fmtMoney(d.imbalance)}. Check posted journal entries for consistency.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
        <FinanceSection title="Assets" accents="default" accounts={d.assets.accounts} total={d.assets.total} />
        <div className="space-y-6">
          <FinanceSection title="Liabilities" accents="amber" accounts={d.liabilities.accounts} total={d.liabilities.total} />
          <FinanceSection title="Equity" accents="purple" accounts={d.equity.accounts} total={d.equity.total} />
        </div>
      </div>

      {/* Bottom assertion */}
      <div className="grid grid-cols-2 gap-6 pt-4 border-t-4 border-ink-900">
        <div className="flex justify-between items-baseline">
          <span className="font-display font-bold text-ink-900">Total assets</span>
          <span className="font-mono font-display font-bold text-xl text-ink-900 tabular-nums tracking-tight">
            {fmtMoney(d.assets.total)}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="font-display font-bold text-ink-900">Liabilities + Equity</span>
          <span className="font-mono font-display font-bold text-xl text-ink-900 tabular-nums tracking-tight">
            {fmtMoney(d.totalLiabilitiesAndEquity)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Cash flow
// ============================================================
function CashFlowReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-cf', startDate, endDate],
    queryFn: () => reportsApi.financeCashFlow({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8 max-w-3xl">
      <ReportHeader title="Cash Flow Statement" period={formatPeriod(startDate, endDate)} />

      <CashFlowSection title="Operating activities" rows={d.operating.rows} total={d.operating.total} />
      <div className="mt-6">
        <CashFlowSection title="Investing activities" rows={d.investing.rows} total={d.investing.total} />
      </div>
      <div className="mt-6">
        <CashFlowSection title="Financing activities" rows={d.financing.rows} total={d.financing.total} />
      </div>

      <div className="mt-6 pt-4 border-t-4 border-ink-900 flex justify-between items-baseline">
        <span className="font-display font-bold text-ink-900">Net change in cash</span>
        <span className={clsx(
          'font-mono font-display font-bold text-xl tabular-nums tracking-tight',
          d.netChangeInCash >= 0 ? 'text-sage-700' : 'text-danger'
        )}>
          {fmtMoney(d.netChangeInCash)}
        </span>
      </div>

      {d.note && (
        <div className="text-xs text-ink-500 italic mt-4 leading-relaxed">{d.note}</div>
      )}
    </div>
  );
}

function CashFlowSection({ title, rows, total }: { title: string; rows: any[]; total: number }) {
  return (
    <div>
      <h3 className="eyebrow text-ink-700 border-b border-ink-300 pb-1 mb-2">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-xs text-ink-400 italic py-2 pl-2">No activity</div>
      ) : (
        <div className="divide-y divide-ink-100">
          {rows.map((r, i) => (
            <div key={i} className="flex justify-between py-1.5 pl-2 text-sm">
              <span className="font-mono text-ink-700">{r.source}</span>
              <span className={clsx(
                'font-mono tabular-nums',
                r.netCashFlow < 0 ? 'text-danger' : 'text-ink-800'
              )}>
                {r.netCashFlow < 0
                  ? `(${fmtMoney(Math.abs(r.netCashFlow)).slice(1)})`
                  : fmtMoney(r.netCashFlow)}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between font-semibold text-sm border-t border-ink-300 pt-2 mt-1">
        <span className="text-ink-900">Total</span>
        <span className="font-mono tabular-nums text-ink-900">{fmtMoney(total)}</span>
      </div>
    </div>
  );
}

// ============================================================
// AR/AP Aging
// ============================================================
function AgingReport({ side, asOfDate }: { side: 'ap'|'ar'; asOfDate: string }) {
  const q = useQuery({
    queryKey: ['rep-aging', side, asOfDate],
    queryFn: () => side === 'ap'
      ? reportsApi.financeApAging({ asOfDate })
      : reportsApi.financeArAging({ asOfDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;
  const partyKey = side === 'ap' ? 'supplierName' : 'customerName';
  const partyLabel = side === 'ap' ? 'Supplier' : 'Customer';
  const title = side === 'ap' ? 'Accounts payable aging' : 'Accounts receivable aging';

  const bucketTone = (label: string) => {
    if (label.toLowerCase().includes('current') || label.includes('0-30') || label.includes('0–30')) return 'sage';
    if (label.includes('31-60') || label.includes('31–60')) return 'default';
    if (label.includes('61-90') || label.includes('61–90')) return 'amber';
    return 'danger';
  };

  return (
    <div>
      <ReportHeader title={title} period={formatAsOf(asOfDate)} />

      {/* Bucket summary */}
      <div className="card p-5 mb-6">
        <div className="eyebrow text-ink-500 mb-3">Aging buckets</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {d.bucketSummary.map((b: any) => {
            const tone = bucketTone(b.label);
            const bar = {
              default: 'bg-ink-200',
              sage: 'bg-sage-500',
              amber: 'bg-amber-500',
              danger: 'bg-danger',
            }[tone];
            return (
              <div key={b.label} className="relative bg-canvas-subtle rounded-xl p-3 overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
                <div className="eyebrow text-ink-500 text-2xs">{b.label}</div>
                <div className="mt-2 font-display font-bold text-xl text-ink-900 tabular-nums">{fmtInt(b.count)}</div>
                <div className="text-2xs font-mono text-ink-500 mt-0.5 tabular-nums">{fmtMoney(b.amount)}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-3 border-t border-ink-100 flex justify-between items-baseline">
          <span className="eyebrow text-ink-700">Total outstanding</span>
          <span>
            <span className="font-display font-bold text-lg text-ink-900 tabular-nums font-mono">
              {fmtMoney(d.totalAmount)}
            </span>
            <span className="text-xs text-ink-500 ml-2 font-mono tabular-nums">
              {fmtInt(d.totalCount)} invoice{d.totalCount === 1 ? '' : 's'}
            </span>
          </span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.rows.length ? <EmptyState message="No outstanding invoices." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Invoice #</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">{partyLabel}</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Invoice date</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Due date</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Total</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Balance</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Days overdue</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Bucket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.rows.map((r: any, i: number) => (
                  <tr key={r.invoiceId} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.invoiceNumber}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{r[partyKey]}</td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {new Date(r.invoiceDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {new Date(r.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtMoney(r.grandTotal)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(r.balanceDue)}</td>
                    <td className={clsx(
                      'px-4 py-2 text-right font-mono text-xs tabular-nums font-semibold',
                      r.daysOverdue > 60 ? 'text-danger' :
                      r.daysOverdue > 30 ? 'text-amber-700' :
                      r.daysOverdue > 0 ? 'text-ink-700' : 'text-sage-700'
                    )}>
                      {r.daysOverdue}
                    </td>
                    <td className="px-4 py-2 text-2xs text-ink-700 font-mono">{r.bucket}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Headcount
// ============================================================
function HeadcountReport() {
  const q = useQuery({ queryKey: ['rep-headcount'], queryFn: () => reportsApi.hrHeadcount() });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Headcount" period="As of today" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total" value={fmtInt(d.summary.total)} />
        <KpiTile label="Active" value={fmtInt(d.summary.active)} accent="sage" />
        <KpiTile label="Suspended" value={fmtInt(d.summary.suspended)}
          accent={d.summary.suspended > 0 ? 'amber' : 'default'} />
        <KpiTile label="Terminated" value={fmtInt(d.summary.terminated)} />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.byDepartment.length ? <EmptyState message="No active employees." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Department</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Total</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Full-time</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Part-time</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Contract / Intern</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.byDepartment.map((r: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-medium text-ink-900">{r.departmentName}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtInt(r.total)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.fullTime)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.partTime)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.contract)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HR turnover
// ============================================================
function TurnoverHrReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-hr-turnover', startDate, endDate],
    queryFn: () => reportsApi.hrTurnover({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Employee turnover" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Beginning headcount" value={fmtInt(d.beginningHeadcount)} />
        <KpiTile label="Ending headcount" value={fmtInt(d.endingHeadcount)} />
        <KpiTile label="Hires" value={fmtInt(d.hires)} accent="sage" />
        <KpiTile label="Terminations" value={fmtInt(d.terminations)}
          accent={d.terminations > 0 ? 'amber' : 'default'} />
      </div>

      <div className="card p-6 mb-6">
        <div className="flex justify-between items-baseline">
          <span className="font-display font-bold text-ink-900">Turnover rate</span>
          <span className="font-mono font-display font-bold text-3xl text-ink-900 tabular-nums tracking-tight">
            {d.turnoverRate}%
          </span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-100">
          <div className="eyebrow text-ink-500">Terminated employees in range</div>
        </div>
        <div className="overflow-x-auto">
          {!d.terminatedEmployees.length ? <EmptyState message="No terminations in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Code</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Name</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Department</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Hire date</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Terminated (approx)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.terminatedEmployees.map((e: any, i: number) => (
                  <tr key={e.id} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{e.employeeCode}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{e.fullName}</td>
                    <td className="px-4 py-2 text-sm text-ink-700">{e.departmentName}</td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {new Date(e.hireDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {new Date(e.approxTerminationDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {d.note && (
        <div className="text-xs text-ink-500 italic mt-3 leading-relaxed">{d.note}</div>
      )}
    </div>
  );
}

// ============================================================
// Attendance summary
// ============================================================
function AttendanceReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-attendance', startDate, endDate],
    queryFn: () => reportsApi.hrAttendanceSummary({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Attendance summary" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiTile label="Employees" value={fmtInt(d.summary.employees)} />
        <KpiTile label="Present" value={fmtInt(d.summary.present)} accent="sage" />
        <KpiTile label="Absent" value={fmtInt(d.summary.absent)}
          accent={d.summary.absent > 0 ? 'amber' : 'default'} />
        <KpiTile label="Late" value={fmtInt(d.summary.late)} />
        <KpiTile label="Hours" value={fmtInt(Math.round(d.summary.hoursWorked))} />
        <KpiTile label="Overtime" value={fmtInt(Math.round(d.summary.overtime))} />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.rows.length ? <EmptyState message="No attendance records in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Code</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Name</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Dept</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-sage-700">Present</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-amber-700">Absent</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Late</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Leave</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Hours</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">OT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.rows.map((r: any, i: number) => (
                  <tr key={r.employeeId} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.employeeCode}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{r.fullName}</td>
                    <td className="px-4 py-2 text-xs text-ink-600">{r.departmentName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-sage-700 font-medium">{fmtInt(r.daysPresent)}</td>
                    <td className={clsx(
                      'px-4 py-2 text-right font-mono text-xs tabular-nums font-medium',
                      r.daysAbsent > 0 ? 'text-amber-700' : 'text-ink-400'
                    )}>
                      {fmtInt(r.daysAbsent)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.daysLate)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.daysOnLeave)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{r.hoursWorked.toFixed(1)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{r.overtimeHours.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Payroll register
// ============================================================
function PayrollRegisterReport() {
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);

  const q = useQuery({
    queryKey: ['rep-payroll', periodYear, periodMonth],
    queryFn: () => reportsApi.hrPayrollRegister({ periodYear, periodMonth }),
  });

  const monthLabel = new Date(periodYear, periodMonth - 1, 1)
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div>
      <ReportHeader title="Payroll register" period={monthLabel} />

      {/* Period picker */}
      <div className="card p-4 mb-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">Year</label>
          <input type="number" className="input w-28 tabular-nums" value={periodYear}
            onChange={(e) => setPeriodYear(parseInt(e.target.value) || new Date().getFullYear())} />
        </div>
        <div>
          <label className="label">Month</label>
          <select className="input w-36" value={periodMonth}
            onChange={(e) => setPeriodMonth(parseInt(e.target.value))}>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2000, i, 1).toLocaleDateString(undefined, { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={5} /></div> :
       q.error ? <ErrorState error={q.error} /> : (
        <div className="space-y-6">
          {q.data.runs.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-ink-100">
                <div className="eyebrow text-ink-500">Payroll runs</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-canvas-subtle border-b border-ink-100">
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Run #</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Period</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Status</th>
                      <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Gross</th>
                      <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {(q.data?.runs ?? []).map((r: any) => (
                      <tr key={r.id}>
                        <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.runNumber}</td>
                        <td className="px-4 py-2 font-mono text-xs text-ink-700 tabular-nums">
                          {r.periodYear}-{String(r.periodMonth).padStart(2, '0')}
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-700 ring-ink-200">
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.totalGross)}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-sage-700 tabular-nums">{fmtMoney(r.totalNet)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-ink-100">
              <div className="eyebrow text-ink-500">Employee detail</div>
            </div>
            <div className="overflow-x-auto">
              {!q.data.lines.length ? <EmptyState message="No payroll runs for this period." /> : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-canvas-subtle border-b border-ink-100">
                      <th className="px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Code</th>
                      <th className="px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Name</th>
                      <th className="px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Dept</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Base</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Allow</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">OT</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Bonus</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-700">Gross</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">Tax</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">SS</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">Ins</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">Pen</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">Other</th>
                      <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-sage-700">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {(q.data?.lines ?? []).map((l: any, i: number) => (
                      <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                        <td className="px-3 py-2 font-mono font-semibold text-ink-900">{l.employeeCode}</td>
                        <td className="px-3 py-2 text-ink-900">{l.fullName}</td>
                        <td className="px-3 py-2 text-ink-600">{l.departmentName}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(l.baseSalary)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(l.allowances)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(l.overtimePay)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(l.bonus)}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(l.grossPay)}</td>
                        <td className="px-3 py-2 text-right font-mono text-danger tabular-nums">{fmtMoney(l.taxDeduction)}</td>
                        <td className="px-3 py-2 text-right font-mono text-danger tabular-nums">{fmtMoney(l.socialSecurity)}</td>
                        <td className="px-3 py-2 text-right font-mono text-danger tabular-nums">{fmtMoney(l.insurance)}</td>
                        <td className="px-3 py-2 text-right font-mono text-danger tabular-nums">{fmtMoney(l.pension)}</td>
                        <td className="px-3 py-2 text-right font-mono text-danger tabular-nums">{fmtMoney(l.otherDeductions)}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-sage-700 tabular-nums">{fmtMoney(l.netPay)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
